import { Controller } from "stimulus";

export default class extends Controller {
  static targets = ['checkbox', 'reminderFields', 'numberInput', 'datePickerInput', 'enableField'];

  static values = {
    dueDate: String,
    startDate: String
  };

  connect() {
    this._boundSyncCalendarFromNumber = this._syncCalendarFromNumber.bind(this);
    this._boundSyncNumberFromCalendar = this._syncNumberFromCalendar.bind(this);
    this._boundOnDueDateChanged = this._onDueDateChanged.bind(this);

    if (this.hasNumberInputTarget) {
      this.numberInputTarget.addEventListener('blur', this._boundSyncCalendarFromNumber);
    }

    if (this.hasDatePickerInputTarget) {
      this.datePickerInputTarget.addEventListener('change', this._boundSyncNumberFromCalendar);
    }

    document.addEventListener('promote:datepicker-change', this._boundOnDueDateChanged);
  }

  disconnect() {
    if (this.hasNumberInputTarget) {
      this.numberInputTarget.removeEventListener('blur', this._boundSyncCalendarFromNumber);
    }

    if (this.hasDatePickerInputTarget) {
      this.datePickerInputTarget.removeEventListener('change', this._boundSyncNumberFromCalendar);
    }

    document.removeEventListener('promote:datepicker-change', this._boundOnDueDateChanged);
  }

  toggleReminderFields() {
    const checked = this.checkboxTarget.checked;

    if (checked) {
      this.reminderFieldsTarget.classList.remove('hidden');
    } else {
      this.reminderFieldsTarget.classList.add('hidden');
    }

    if (this.hasEnableFieldTarget) {
      this.enableFieldTarget.value = checked ? 'on' : '';
    }
  }

  clearReminder(event) {
    event.preventDefault();
    this._dispatchDatePickerChange('');
    this._dispatchNumberChange('');
  }

  _onDueDateChanged(event) {
    const newDueDate = event.detail.value;
    const previousDueDate = this.dueDateValue;
    this.dueDateValue = newDueDate;

    const form = this.element.querySelector('form');
    if (form) {
      if (newDueDate === '') {
        form.classList.add('hidden');
      } else {
        form.classList.remove('hidden');

        if (previousDueDate === '') {
          this._resetReminderFields();
        }
      }
    }

    if (!this.hasNumberInputTarget) return;

    const daysBefore = parseInt(this.numberInputTarget.value);

    if (newDueDate === '' || isNaN(daysBefore) || daysBefore < 1) return;

    const dueDate = new Date(newDueDate);
    const reminderDate = new Date(dueDate);
    reminderDate.setDate(reminderDate.getDate() - daysBefore);

    this._dispatchDatePickerChange(this._formatDate(reminderDate));
    this._updateDatePickerMax(newDueDate);
  }

  _syncCalendarFromNumber(event) {
    const daysBefore = parseInt(event.target.value);
    if (isNaN(daysBefore) || daysBefore < 1 || this.dueDateValue === '') {
      return;
    }

    const dueDate = new Date(this.dueDateValue);
    const reminderDate = new Date(dueDate);
    reminderDate.setDate(reminderDate.getDate() - daysBefore);

    this._dispatchDatePickerChange(this._formatDate(reminderDate));
  }

  _syncNumberFromCalendar() {
    if (this.dueDateValue === '' || !this.hasNumberInputTarget) return;

    const selectedValue = this.datePickerInputTarget.value;

    if (!selectedValue) return;

    const dueDate = new Date(this.dueDateValue);
    const selectedDate = new Date(selectedValue);
    const oneDay = 1000 * 3600 * 24;
    const diffDays = Math.round((dueDate.getTime() - selectedDate.getTime()) / oneDay);

    if (diffDays >= 1) {
      this.numberInputTarget.value = diffDays;
      this._dispatchNumberChange(diffDays);
    }
  }

  _resetReminderFields() {
    if (this.hasCheckboxTarget) {
      this.checkboxTarget.checked = false;
    }

    if (this.hasReminderFieldsTarget) {
      this.reminderFieldsTarget.classList.add('hidden');
    }

    if (this.hasEnableFieldTarget) {
      this.enableFieldTarget.value = '';
    }

    if (this.hasNumberInputTarget) {
      this.numberInputTarget.value = '';
    }

    this._dispatchDatePickerChange('');
  }

  _updateDatePickerMax(newDueDate) {
    if (this.hasDatePickerInputTarget) {
      this.datePickerInputTarget.max = newDueDate;
    }
  }

  _dispatchDatePickerChange(value) {
    if (this.hasDatePickerInputTarget) {
      this.datePickerInputTarget.dispatchEvent(new CustomEvent('promote:datepicker-change', {
        detail: { value: value }
      }));
    }
  }

  _dispatchNumberChange(value) {
    if (this.hasNumberInputTarget) {
      this.numberInputTarget.dispatchEvent(new CustomEvent('change', {
        detail: { value: value }
      }));
    }
  }

  _formatDate(date) {
    return date.toISOString().slice(0, 10);
  }
}
